import React, { ReactNode, useCallback, useMemo } from 'react'

import { AxisProps } from '@nivo/axes'
import { ResponsiveBar, BarDatum, BarTooltipProps, BarSvgProps } from '@nivo/bar'
import { OrdinalColorScaleConfigScheme } from '@nivo/colors'
import { ResponsiveProps } from '@nivo/core'
import { Card } from 'antd'
import styled from 'styled-components'

import EmptyState from 'src/components/EmptyState'
import ChartTooltip from 'src/components/Visualizations/ChartTooltip'
import DashboardLabel from 'src/components/Visualizations/DashboardLabel'
import { ChartType } from 'src/components/Visualizations/util'
import Flex from 'src/govwell-ui/components/Flex/Flex'
import useNivoChartFormatSettings from 'src/hooks/use-nivo-chart-format-settings'

const BarChartCard = styled(Card)<{
  $layout?: 'horizontal' | 'vertical'
  $flexBasis?: React.CSSProperties['flexBasis']
  $minWidth?: React.CSSProperties['minWidth']
}>`
  width: ${({ $layout }) => ($layout === 'horizontal' ? '100%' : 'auto')};
  flex-basis: ${({ $flexBasis, $layout }) =>
    $layout === 'horizontal' ? 'auto' : ($flexBasis ?? 'auto')};
  min-width: ${({ $minWidth, $layout }) =>
    $layout === 'horizontal' ? '100%' : ($minWidth ?? 'auto')};
  min-width: ${({ $minWidth, $layout }) =>
    $layout === 'horizontal' ? '100%' : $minWidth ? `max(34%, ${$minWidth})` : 'auto'};
`

const BarChartContainer = styled.div<{ $height: React.CSSProperties['height'] }>`
  width: 100%;
  height: ${({ $height }) => $height};
`

const DEFAULT_COLORS: OrdinalColorScaleConfigScheme = {
  scheme: 'paired',
}

const DEFAULT_COLOR = ['#4096FF']

const OTHERS_KEY = 'Others'

type BarChartProps<TData extends BarDatum> = Omit<
  ResponsiveProps<BarSvgProps<TData>>,
  'valueFormat' | 'ariaLabel'
> & {
  bottomAxisLabel: string
  formatLeftAxisLabel?: (value: string) => string
  isLoading?: boolean
  leftAxisLabel?: string
  label?: React.ReactNode
  labelTooltip?: string
  renderTooltip?: (barTooltipProps: BarTooltipProps<TData>) => ReactNode | string
  shouldHideLeftAxis?: boolean
  shouldTooltipShowXAxisLabel?: boolean
  valueFormat?: (value: number) => string
  isTimeSeries?: boolean
}

function BarChart<TData extends BarDatum>(props: BarChartProps<TData>) {
  const {
    bottomAxisLabel,
    leftAxisLabel,
    valueFormat,
    colors,
    label,
    labelTooltip,
    formatLeftAxisLabel,
    shouldHideLeftAxis,
    shouldTooltipShowXAxisLabel,
    isTimeSeries = false,
    isLoading,
    data: propData,
    keys: propKeys,
    renderTooltip: propRenderTooltip,
    ...barChartProps
  } = props
  const { layout, indexBy } = barChartProps

  const getIndex = useCallback(
    (bar?: TData) => {
      if (!bar) {
        return ''
      }
      const index = typeof indexBy === 'string' ? bar[indexBy] : indexBy?.(bar)
      return index?.toString() ?? ''
    },
    [indexBy]
  )

  const renderTooltip = useCallback(
    (barTooltipProps: BarTooltipProps<TData>) => {
      let tooltipValue: ReactNode | string
      if (propRenderTooltip) {
        tooltipValue = propRenderTooltip(barTooltipProps)
      } else {
        // If there is only one group per bar, show the X axis label
        const tooltipLabel = shouldTooltipShowXAxisLabel
          ? barTooltipProps.indexValue
          : barTooltipProps.id

        tooltipValue = `${tooltipLabel}: ${barTooltipProps.formattedValue}`
      }
      const tooltip = {
        id: String(barTooltipProps.id),
        value: tooltipValue,
      }
      return <ChartTooltip tooltips={[tooltip]} />
    },
    [shouldTooltipShowXAxisLabel, propRenderTooltip]
  )

  const barColors = colors ?? (propKeys && propKeys?.length > 1 ? DEFAULT_COLORS : DEFAULT_COLOR)

  // If there are more than 10 keys, consolidate all other keys into a single key called "Others"
  const computedKeys = useMemo(() => {
    if (!propKeys) {
      return []
    }
    if (propKeys.length <= 10) {
      return propKeys
    }

    const keyTotals: number[] = new Array(propKeys.length).fill(0)
    for (const bar of propData) {
      for (let i = 0; i < propKeys.length; i++) {
        const key = propKeys[i]
        if (key) {
          const value = bar[key]
          if (typeof value === 'number') {
            keyTotals[i] = (keyTotals[i] ?? 0) + value
          }
        }
      }
    }

    const topKeys = propKeys
      .map((k, i) => ({ key: k, total: keyTotals[i] ?? 0 }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map((k) => k.key)

    return [...topKeys, 'Others']
  }, [propKeys, propData])

  // Consolidate smaller keys into a single bar called "Others" if there are more than 10 keys
  const dataWithConsolidatedKeys = useMemo(() => {
    if (!propKeys || propKeys.length <= 10) {
      return propData
    }

    const topKeysSet = new Set(computedKeys.slice(0, 10))

    return propData.map((bar) => {
      const newBar: Record<string, string | number> = { ...bar }
      let others = 0
      for (const key of propKeys) {
        const value = bar[key]
        if (topKeysSet.has(key)) {
          continue
        }
        if (typeof value === 'number') {
          others += value
        }
        delete newBar[key] // Remove the consolidated key from the bar
      }
      newBar[OTHERS_KEY] = others
      return newBar as TData
    })
  }, [propData, propKeys, computedKeys])

  const getKeyValuesSum = useCallback(
    (bar: TData) => {
      return (
        computedKeys?.reduce((sum, key) => {
          const value = bar[key]
          if (typeof value === 'number') {
            return sum + value
          }
          return sum
        }, 0) ?? 0
      )
    },
    [computedKeys]
  )

  // Add a temporary sum property to the data
  const dataWithSum = useMemo(() => {
    return dataWithConsolidatedKeys.map((bar) => ({
      bar,
      sum: getKeyValuesSum(bar),
    }))
  }, [dataWithConsolidatedKeys, getKeyValuesSum])

  // Sort the data by the sum of the keys if it's not a time series
  const sortedData = useMemo(() => {
    return !isTimeSeries ? dataWithSum.sort((a, b) => b.sum - a.sum) : dataWithSum
  }, [isTimeSeries, dataWithSum])

  const ariaLabel = useMemo(() => {
    const barValues: string[] = []
    for (const element of sortedData) {
      const index = getIndex(element.bar)
      if (index) {
        const barValue = element.sum
        const formattedBarValue = valueFormat ? valueFormat(barValue) : barValue
        barValues.push(`Column: ${index}, Value: ${formattedBarValue}`)
      }
    }
    return `A bar chart showing ${label}.  Values by bar: ${barValues.join(', ')}`
  }, [label, sortedData, getIndex, valueFormat])

  // Remove the temporary sum property from the data
  const data = useMemo(() => {
    return sortedData.map((element) => element.bar)
  }, [sortedData])

  const settings = useNivoChartFormatSettings({
    chartType: ChartType.Bar,
    data,
    getIndex: getIndex as (bar?: BarDatum) => string,
    keys: computedKeys,
    layout,
    formatLeftAxisLabel,
  })

  const {
    bottomAxisFormatSettings,
    leftAxisLegendOffset,
    margins,
    minWidthPx,
    flexBasisPx,
    isEmpty,
    theme,
  } = settings

  const minWidth = `${minWidthPx}px`
  const flexBasis = `${flexBasisPx}px`

  const barChartHeight = layout === 'horizontal' ? '100px' : '300px'

  const leftAxis: AxisProps | null = useMemo(() => {
    if (shouldHideLeftAxis) {
      return null
    }
    return {
      tickSize: 0, // Disable showing ticks next to the axis labels
      legend: leftAxisLabel,
      legendOffset: leftAxisLegendOffset,
      legendPosition: 'middle', // Center the axis label
      format: formatLeftAxisLabel,
    }
  }, [shouldHideLeftAxis, leftAxisLabel, leftAxisLegendOffset, formatLeftAxisLabel])

  const bottomAxis: AxisProps | null = useMemo(
    () => ({
      tickSize: 0, // Disable showing ticks next to the axis labels
      legend: bottomAxisLabel,
      legendOffset: bottomAxisFormatSettings?.bottomAxisLegendOffset,
      legendPosition: 'middle', // Center the axis label
      tickRotation: bottomAxisFormatSettings?.bottomAxisLabelRotation, // Rotate the axis label if the screen is small
      format: bottomAxisFormatSettings?.bottomAxisLabelFormat,
    }),
    [bottomAxisFormatSettings, bottomAxisLabel]
  )

  return (
    <BarChartCard
      $layout={layout}
      $flexBasis={flexBasis}
      $minWidth={minWidth}
      className="flex-shrink flex-grow"
    >
      <Flex direction="column" gap={4}>
        <DashboardLabel label={label} tooltip={labelTooltip} isBold />
        <BarChartContainer $height={barChartHeight}>
          {!isLoading && isEmpty ? (
            <EmptyState hideBorder>
              <EmptyState.Image />
              <EmptyState.Message>There is no data for the selected filters.</EmptyState.Message>
            </EmptyState>
          ) : (
            <ResponsiveBar
              {...barChartProps}
              data={data}
              keys={computedKeys}
              ariaLabel={ariaLabel}
              tooltip={renderTooltip} // Show the custom tooltip on hover
              valueFormat={valueFormat}
              isInteractive={true} // Allow bar group details to be shown on hover
              enableLabel={false} // Disable showing the count per bar group
              enableGridY={layout !== 'horizontal'} // Show horizontal grids for vertical layout
              colors={barColors}
              axisBottom={bottomAxis}
              axisLeft={leftAxis}
              margin={margins}
              theme={theme}
            />
          )}
        </BarChartContainer>
      </Flex>
    </BarChartCard>
  )
}

export default React.memo(BarChart)
